/**
 * UnifiedScanInput — Drop-in scanner used everywhere we need to identify a
 * person from a QR / badge / numeric ID. Calls `/api/scan/identify` and
 * surfaces the resolved profile to the parent via `onIdentified`.
 *
 * Modes (toggle):
 *   • Camera scan (preferred on mobile / phones / tablets)
 *   • Manual entry (paste an ID, admission number, badge code, or email)
 *
 * Always shows the resolved profile card so the operator can confirm before
 * acting. Emits {profile, raw} on success so callers can use either.
 */
import React, { useState } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Camera, Type, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedScanInput({ onIdentified, autoSubmitOnScan = true, contextId }) {
  const [mode, setMode] = useState('camera'); // 'camera' | 'manual'
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState('');

  const identify = async (value) => {
    if (!value) return;
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/scan/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ value, context_id: contextId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Lookup failed (${res.status})`);
      if (!data.found) {
        setError('Not recognised. Try the manual ID or scan a different badge.');
        toast.error('QR not recognised');
        return;
      }
      setResolved(data);
      onIdentified?.(data);
      toast.success(`Identified: ${data.profile.name}`);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleManual = () => identify(manual.trim());
  const handleScan = (codes) => {
    if (!codes?.length || busy) return;
    const value = codes[0]?.rawValue || '';
    if (!value) return;
    if (autoSubmitOnScan) {
      identify(value);
    } else {
      setManual(value);
      setMode('manual');
    }
  };

  const reset = () => { setResolved(null); setError(''); setManual(''); };

  if (resolved) {
    const p = resolved.profile;
    return (
      <Card className="border-emerald-300 bg-emerald-50/50" data-testid="scan-resolved-card">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 overflow-hidden">
            {p.photo
              ? <img src={p.photo} alt="" className="h-full w-full object-cover" />
              : <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{p.name}</div>
            <div className="text-xs text-slate-600 flex flex-wrap gap-x-2">
              <span className="capitalize">{p.kind}</span>
              {p.admission_number && <span>· #{p.admission_number}</span>}
              {p.class_name && <span>· {p.class_name}</span>}
              {p.tenant_name && <span>· {p.tenant_name}</span>}
              {p.event_name && <span>· {p.event_name}</span>}
            </div>
            {resolved.active_incidents?.length > 0 && (
              <div className="mt-1 text-[11px] text-rose-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {resolved.active_incidents.length} open incident{resolved.active_incidents.length === 1 ? '' : 's'}
              </div>
            )}
            {resolved.active_gate_pass && (
              <div className="mt-1 text-[11px] text-amber-700">
                Gate pass active · {resolved.active_gate_pass.status}
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={reset} title="Scan another" data-testid="scan-reset-btn">
            <X className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="unified-scan-input">
      <div className="flex gap-2">
        <Button
          type="button" variant={mode === 'camera' ? 'default' : 'outline'} size="sm"
          onClick={() => setMode('camera')} data-testid="scan-mode-camera"
        >
          <Camera className="h-4 w-4 mr-1" /> Camera
        </Button>
        <Button
          type="button" variant={mode === 'manual' ? 'default' : 'outline'} size="sm"
          onClick={() => setMode('manual')} data-testid="scan-mode-manual"
        >
          <Type className="h-4 w-4 mr-1" /> Manual
        </Button>
      </div>

      {mode === 'camera' ? (
        <div className="rounded-md overflow-hidden border bg-slate-900 max-w-md">
          <Scanner
            onScan={handleScan}
            onError={(err) => console.warn('[scan err]', err)}
            constraints={{ facingMode: 'environment' }}
            styles={{ container: { width: '100%' } }}
            classNames={{ container: 'aspect-square' }}
          />
          <p className="text-xs text-white/70 p-2 text-center">Point at a student/delegate badge</p>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="Paste ID / badge code / admission number / email"
            onKeyDown={(e) => e.key === 'Enter' && handleManual()}
            data-testid="scan-manual-input"
          />
          <Button onClick={handleManual} disabled={busy || !manual.trim()} data-testid="scan-manual-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Identify'}
          </Button>
        </div>
      )}

      {error && (
        <div className="text-xs text-rose-600 flex items-center gap-1" data-testid="scan-error">
          <AlertCircle className="h-3 w-3" /> {error}
        </div>
      )}
    </div>
  );
}
